import * as React from 'react';
import './TableScroll.css';

interface TableScrollProps {
  /** 変えた列の幅をブラウザに保存するときの名前。画面ごと・表ごとに一意にする */
  storageKey: string;
  className?: string;
}

const STORAGE_PREFIX = 'eve-web:column-widths:';
const MIN_COLUMN_WIDTH = 60;
// 列の境目のつかみ所の幅。境目の線を中心に置く
const HANDLE_WIDTH = 8;

/**
 * 列の幅の指定。幅いっぱいの表は、画面の大きさが変わっても幅いっぱいのままにするため、表の幅に対する割合（percent）で持つ。
 * 中身の幅の表は、幅を変え始めたときの表の幅を保つため、ピクセル（px）で持つ。
 */
interface ColumnLayout {
  unit: 'percent' | 'px';
  widths: number[];
}

interface HandlePosition {
  /** 境目の左の列の番号。この列と右隣の列の間で幅をやりとりする */
  index: number;
  left: number;
  top: number;
  height: number;
}

function isColumnLayout(value: unknown): value is ColumnLayout {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const layout = value as { unit?: unknown; widths?: unknown };
  const isUnit = layout.unit === 'percent' || layout.unit === 'px';
  if (!isUnit || !Array.isArray(layout.widths)) {
    return false;
  }
  return layout.widths.every((width) => typeof width === 'number');
}

function loadLayout(storageKey: string): ColumnLayout | undefined {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + storageKey);
    if (!raw) {
      return undefined;
    }
    const parsed: unknown = JSON.parse(raw);
    // 形式の合わない値（以前の形式で保存した幅など）は読み込まず、初期の列の幅で表示する
    if (!isColumnLayout(parsed)) {
      return undefined;
    }
    return parsed;
  } catch (e) {
    return undefined;
  }
}

function saveLayout(storageKey: string, layout: ColumnLayout) {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(layout));
  } catch (e) {
    // 保存できない環境（プライベートブラウズなど）では、その場の表示だけ変える
  }
}

function clearSavedLayout(storageKey: string) {
  try {
    window.localStorage.removeItem(STORAGE_PREFIX + storageKey);
  } catch (e) {
    // 保存できない環境では消すものもない
  }
}

function findTable(container: HTMLDivElement | null): HTMLTableElement | null {
  if (!container) {
    return null;
  }
  return container.querySelector(':scope > table');
}

/** 幅を変える単位になる見出しのセル。thead の最初の行のセルを使う */
function getHeaderCells(table: HTMLTableElement): HTMLTableCellElement[] {
  if (!table.tHead || table.tHead.rows.length === 0) {
    return [];
  }
  return Array.from(table.tHead.rows[0].cells);
}

/**
 * 幅を変えられる列か。見出しのセルに data-column-resize="off" を付けた列（物件一覧の選択の列など）は幅を変えず、CSSで決めた幅のまま保つ。
 * 幅を変えない列には、CSSで幅を指定しておく
 */
function isResizableCell(cell: HTMLTableCellElement | undefined): boolean {
  if (!cell) {
    return false;
  }
  return cell.dataset.columnResize !== 'off';
}

/** 列の幅を <col> で決めている表では、見出しのセルではなく <col> の幅を変える */
function getColElements(table: HTMLTableElement, cellCount: number): HTMLTableColElement[] {
  const cols = Array.from(table.querySelectorAll<HTMLTableColElement>(':scope > colgroup > col'));
  if (cols.length !== cellCount) {
    return [];
  }
  return cols;
}

function toCssWidth(layout: ColumnLayout, width: number): string {
  if (layout.unit === 'percent') {
    return `${width}%`;
  }
  return `${width}px`;
}

/**
 * 列の幅を指定どおりに保つため、表を固定の列幅にして各列の幅を決める。
 * ピクセルで持つ表は、表の幅も列の幅の合計にする。割合で持つ表は、表の幅をCSSの指定（幅いっぱい）のまま変えない。
 */
function applyLayout(table: HTMLTableElement, layout: ColumnLayout) {
  const cells = getHeaderCells(table);
  const cols = getColElements(table, cells.length);
  table.style.tableLayout = 'fixed';
  table.classList.add('TableScroll-fixed');
  if (layout.unit === 'px') {
    const total = layout.widths.reduce((sum, width) => sum + width, 0);
    table.style.width = `${total}px`;
  } else {
    table.style.width = '';
    // 割合で持つ表は画面が狭いと列も狭まるので、いちばん狭い列が最小の幅を下回らない表の幅を最小の幅にし、それより狭い画面では横にスクロールさせる。
    // CSS で決めた最小の幅（物件一覧の900pxなど）のほうが大きければ、そちらを使う
    table.style.minWidth = '';
    const cssMinWidth = parseFloat(window.getComputedStyle(table).minWidth) || 0;
    // 幅を変えない列はCSSで決めた幅のまま縮まないので、いちばん狭い列を探すときに含めない
    const resizableWidths = layout.widths.filter((_width, index) => isResizableCell(cells[index]));
    const narrowestPercent = Math.min(...resizableWidths);
    const requiredMinWidth = (MIN_COLUMN_WIDTH * 100) / narrowestPercent;
    table.style.minWidth = `${Math.ceil(Math.max(cssMinWidth, requiredMinWidth))}px`;
  }
  // 割合で持つ表では、幅を変えられる列のうち最後の列には幅を指定せず、残りの幅をすべて充てる。
  // 幅を変えない列（ピクセルの幅）と割合の列が混ざると、ブラウザが表の幅を入れ物より狭く計算することがあるため、
  // 残りを受け持つ列を1つ置いて、表の幅を必ず幅いっぱいに保つ
  let remainderIndex = -1;
  if (layout.unit === 'percent') {
    cells.forEach((cell, index) => {
      if (isResizableCell(cell)) {
        remainderIndex = index;
      }
    });
  }
  layout.widths.forEach((width, index) => {
    // 幅を変えない列は、CSSで決めた幅のまま保つ
    if (!isResizableCell(cells[index])) {
      return;
    }
    let cssWidth = toCssWidth(layout, width);
    if (index === remainderIndex) {
      cssWidth = 'auto';
    }
    if (cols.length > 0) {
      cols[index].style.width = cssWidth;
      return;
    }
    if (cells[index]) {
      // 測った幅はセルの左右の余白を含むので、指定する幅も余白を含む数え方にする
      cells[index].style.boxSizing = 'border-box';
      cells[index].style.width = cssWidth;
    }
  });
}

/** 列の幅の指定を外し、CSS で決めた初期の列の幅に戻す */
function clearAppliedLayout(table: HTMLTableElement) {
  const cells = getHeaderCells(table);
  const cols = getColElements(table, cells.length);
  table.style.tableLayout = '';
  table.style.width = '';
  table.style.minWidth = '';
  table.classList.remove('TableScroll-fixed');
  cols.forEach((col) => {
    col.style.width = '';
  });
  cells.forEach((cell) => {
    cell.style.boxSizing = '';
    cell.style.width = '';
  });
}

function measureWidths(table: HTMLTableElement): number[] {
  return getHeaderCells(table).map((cell) => cell.getBoundingClientRect().width);
}

/** ピクセルの列の幅を、指定する単位の値にする。割合は列の幅の合計に対する割合にし、合計がちょうど100%になるようにする */
function toLayout(unit: ColumnLayout['unit'], pixelWidths: number[]): ColumnLayout {
  if (unit === 'px') {
    return { unit, widths: pixelWidths };
  }
  const total = pixelWidths.reduce((sum, width) => sum + width, 0);
  return { unit, widths: pixelWidths.map((width) => (width / total) * 100) };
}

/**
 * 境目を動かしたときに狭める列の番号を、動かす向きの端から境目に向かう順に並べる。右へ動かすときは右側の列、左へ動かすときは左側の列。
 * 端の列から狭めるので、境目の隣の列は幅を保ったまま押し出され、端の列が最小の幅に達したら次に端に近い列を狭める
 */
function getShrinkOrder(columnCount: number, index: number, distance: number): number[] {
  const order: number[] = [];
  if (distance > 0) {
    for (let i = columnCount - 1; i > index; i--) {
      order.push(i);
    }
    return order;
  }
  for (let i = 0; i <= index; i++) {
    order.push(i);
  }
  return order;
}

/** 境目を動かしたときに広げる列の番号。右へ動かすときは境目の左の列、左へ動かすときは境目の右の列 */
function getGrowIndex(index: number, distance: number): number {
  if (distance > 0) {
    return index;
  }
  return index + 1;
}

/**
 * 境目（index の列と右隣の列の間）を distance だけ動かしたときの列の幅。表全体の幅は変えない。
 * 動かす向きの端の列から最小の幅まで狭め、狭めた分の合計を反対側の隣の列に足す。境目と端の間の列は幅を保ったまま押し出される。
 * 端の列が最小の幅に達したら、次に端に近い列を狭める。幅を変えない列は狭める対象にしない
 */
function moveBoundary(startWidths: number[], resizable: boolean[], index: number, distance: number): number[] {
  const widths = startWidths.slice();
  const shrinkOrder = getShrinkOrder(widths.length, index, distance).filter((i) => resizable[i]);
  const growIndex = getGrowIndex(index, distance);

  let remaining = Math.abs(distance);
  let shrunk = 0;
  shrinkOrder.forEach((i) => {
    if (remaining <= 0) {
      return;
    }
    const available = Math.max(0, widths[i] - MIN_COLUMN_WIDTH);
    const amount = Math.min(available, remaining);
    widths[i] -= amount;
    remaining -= amount;
    shrunk += amount;
  });
  widths[growIndex] += shrunk;
  return widths;
}

/**
 * 列と列の間の境目に置くつかみ所の位置。表全体の幅は変えないので、表の右端（最後の列の右）には置かない。
 * 境目の左右のどちらかが幅を変えない列なら、その境目には置かない。
 * 入れ物の中の位置なので、横にスクロールしても境目に付いて動く。
 */
function measureHandlePositions(table: HTMLTableElement): HandlePosition[] {
  const head = table.tHead;
  if (!head) {
    return [];
  }
  const cells = getHeaderCells(table);
  const positions: HandlePosition[] = [];
  cells.forEach((cell, index) => {
    const isBetweenResizableColumns = isResizableCell(cell) && isResizableCell(cells[index + 1]);
    if (!isBetweenResizableColumns) {
      return;
    }
    positions.push({
      index,
      left: table.offsetLeft + cell.offsetLeft + cell.offsetWidth - HANDLE_WIDTH / 2,
      top: table.offsetTop + head.offsetTop,
      height: head.offsetHeight,
    });
  });
  return positions;
}

function isSamePositions(a: HandlePosition[], b: HandlePosition[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((position, index) => {
    const other = b[index];
    const isSameColumn = position.index === other.index;
    return isSameColumn && position.left === other.left && position.top === other.top && position.height === other.height;
  });
}

/**
 * 表を囲む入れ物。表が画面に収まらないときは表だけを横にスクロールさせ、列と列の間の境目をドラッグして列の幅を変えられるようにする。
 * 境目をドラッグすると、表全体の幅は変えずに、境目の左の列と右の列の間で幅をやりとりする。
 * 変えた幅は storageKey ごとにブラウザに保存し、次に開いたときも同じ幅で表示する。境目をダブルクリックすると初期の列の幅に戻す。
 * 幅を変えるまでは表のレイアウトに手を加えない。
 */
export const TableScroll: React.FC<TableScrollProps> = function (props) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  // 当てている列の幅の指定。幅を変える前と、初期の幅に戻した後は undefined
  const layoutRef = React.useRef<ColumnLayout>();
  const [handlePositions, setHandlePositions] = React.useState<HandlePosition[]>([]);
  const [draggingIndex, setDraggingIndex] = React.useState<number>();

  const updateHandlePositions = React.useCallback(() => {
    const table = findTable(containerRef.current);
    if (!table) {
      setHandlePositions([]);
      return;
    }
    const next = measureHandlePositions(table);
    setHandlePositions((current) => {
      if (isSamePositions(current, next)) {
        return current;
      }
      return next;
    });
  }, []);

  // 表が描かれたら保存した幅を当て、つかみ所の位置を表に合わせる。表は読み込みの後から現れることがあるので描画のたびに確かめる
  React.useLayoutEffect(() => {
    const table = findTable(containerRef.current);
    if (table && !layoutRef.current) {
      const saved = loadLayout(props.storageKey);
      const cellCount = getHeaderCells(table).length;
      if (saved && cellCount > 0 && saved.widths.length === cellCount) {
        applyLayout(table, saved);
        layoutRef.current = saved;
      }
    }
    updateHandlePositions();
  });

  // 画面の大きさや表の中身が変わると境目の位置も変わるので、つかみ所を置き直す
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const observer = new MutationObserver(() => updateHandlePositions());
    observer.observe(container, { childList: true, subtree: true, characterData: true, attributes: true });
    window.addEventListener('resize', updateHandlePositions);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateHandlePositions);
    };
  }, [updateHandlePositions]);

  const startResize = (index: number, event: React.MouseEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    const table = findTable(container);
    if (!container || !table) {
      return;
    }
    event.preventDefault();
    // 初めて幅を変えるときは、表が入れ物の幅いっぱいなら割合で、そうでなければピクセルで持つ
    let unit: ColumnLayout['unit'] = 'px';
    if (layoutRef.current) {
      unit = layoutRef.current.unit;
    } else if (table.getBoundingClientRect().width >= container.clientWidth - 1) {
      unit = 'percent';
    }
    // 今見えている列の幅で表を固定してから、境目を動かした分だけ列の幅をやりとりする
    const startWidths = measureWidths(table);
    const resizable = getHeaderCells(table).map((cell) => isResizableCell(cell));
    let layout = toLayout(unit, startWidths);
    applyLayout(table, layout);
    layoutRef.current = layout;
    const startX = event.clientX;
    setDraggingIndex(index);
    document.body.classList.add('TableScroll-resizing');

    const onMouseMove = (moveEvent: MouseEvent) => {
      const widths = moveBoundary(startWidths, resizable, index, moveEvent.clientX - startX);
      layout = toLayout(unit, widths);
      applyLayout(table, layout);
      layoutRef.current = layout;
      updateHandlePositions();
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.classList.remove('TableScroll-resizing');
      setDraggingIndex(undefined);
      saveLayout(props.storageKey, layout);
      updateHandlePositions();
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // 末尾を省略しているセルにマウスを重ねたら、セルの全体の文字を title で出す。省略していないセルには出さない。
  // ページが自分で title を付けたセル（台数使用状況の端末の識別子など）は、そのまま残す
  const showOverflowTitle = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const cell = target.closest('td, th');
    if (!(cell instanceof HTMLTableCellElement) || !containerRef.current || !containerRef.current.contains(cell)) {
      return;
    }
    const hasOwnTitle = cell.title !== '' && cell.dataset.overflowTitle !== 'true';
    if (hasOwnTitle) {
      return;
    }
    const isOverflowing = cell.scrollWidth > cell.clientWidth;
    if (isOverflowing) {
      cell.title = cell.innerText.trim();
      cell.dataset.overflowTitle = 'true';
      return;
    }
    if (cell.dataset.overflowTitle === 'true') {
      cell.removeAttribute('title');
      delete cell.dataset.overflowTitle;
    }
  };

  const resetWidths = () => {
    const table = findTable(containerRef.current);
    if (!table) {
      return;
    }
    clearSavedLayout(props.storageKey);
    clearAppliedLayout(table);
    layoutRef.current = undefined;
    updateHandlePositions();
  };

  let className = 'App-table_scroll TableScroll';
  if (props.className) {
    className += ` ${props.className}`;
  }

  return (
    <div className={className} ref={containerRef} onMouseOver={showOverflowTitle}>
      {props.children}
      {handlePositions.map((position) => {
        const index = position.index;
        let handleClassName = 'TableScroll-handle';
        if (draggingIndex === index) {
          handleClassName += ' TableScroll-handle_dragging';
        }
        return (
          <div
            key={index}
            className={handleClassName}
            style={{ left: position.left, top: position.top, height: position.height, width: HANDLE_WIDTH }}
            role="separator"
            aria-orientation="vertical"
            aria-label="列の幅を変更（ダブルクリックで元の幅に戻す）"
            title="ドラッグで列の幅を変更、ダブルクリックで元の幅に戻す"
            onMouseDown={(event) => startResize(index, event)}
            onDoubleClick={() => resetWidths()}
          />
        );
      })}
    </div>
  );
};
